import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import { Check } from "lucide-react";

interface BespokeSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const features = [
  {
    title: "Personal Consultation",
    description: "One-on-one sessions with our design team to understand your vision and preferences."
  },
  {
    title: "Custom Measurements",
    description: "Precise measurements to ensure a perfect fit for your unique body type."
  },
  {
    title: "Fabric Selection",
    description: "Choose from our curated collection of premium fabrics and materials."
  },
  {
    title: "Design Customization",
    description: "Personalize details like colors, patterns, and embellishments."
  }
];

export function BespokeSheet({ isOpen, onClose }: BespokeSheetProps) {
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div className="px-4">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-3 tracking-wide font-cinzel">
            Bespoke Personalization
          </h2>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Experience the art of personalized luxury with our bespoke service.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid md:grid-cols-2 gap-8 mb-8"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="flex items-center gap-3 mb-2">
                <Check className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-cinzel font-bold text-primary">{feature.title}</h3>
              </div>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-8 text-center"
        >
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Crafted from the finest premium cotton and designed with precision, each personalized piece reflects the essence of sophistication and exclusivity. Elevate your wardrobe with designs that tell your story—because luxury is personal.
          </p>
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-primary/5 backdrop-blur-sm rounded-2xl p-6 text-center"
        >
          <p className="text-sm text-muted-foreground mb-3">
            Ready to begin your bespoke journey? Our luxury consultants are here to assist you.
          </p>
          <p className="text-sm font-medium text-primary">
            Contact us at{" "}
            <a 
              href="mailto:cs@teluasegh.com"
              className="hover:underline"
            >
              cs@teluasegh.com
            </a>
          </p>
        </motion.div>
      </div>
    </BottomSheet>
  );
} 